/**
 * Host-targeted requests as cards; the badge counts `blocking` ones so a host can see why
 * the game is waiting. Renders whatever the server sent, `InputSpec` or `DecisionSpec`.
 * A driving TV renders it too, which is what `kinds` is for: keep grading lists off it.
 */

import type { Json, RequestId, RequestView } from '@gsf/contract';
import { rendererFor } from './registry';
import type { NameOf, RendererSlots } from './renderer';

export interface JudgmentTrayProps {
  /** `snapshot.requests`. Already narrowed to this viewer by the server. */
  requests: readonly RequestView[];
  onDecide: (requestId: RequestId, value: Json) => void;
  nameOf?: NameOf;
  /** Restrict to these `spec.kind`s. Omit for every card. */
  kinds?: readonly string[];
  /** Wrap the cards in a titled panel with the blocking badge. */
  heading?: string;
  size?: 'md' | 'lg';
  /** Per-item game affordances for list-shaped asks (`RendererSlots`). */
  slots?: RendererSlots;
}

const NO_NAMES: NameOf = () => null;

export function JudgmentTray({
  requests,
  onDecide,
  nameOf = NO_NAMES,
  kinds,
  heading,
  size = 'md',
  slots,
}: JudgmentTrayProps) {
  const shown = kinds ? requests.filter((one) => kinds.includes(one.spec.kind)) : requests;
  if (shown.length === 0) return null;

  const blocking = shown.filter((one) => one.blocking).length;

  const cards = shown.map((request) => {
    const Renderer = rendererFor(request.spec.kind);
    if (!Renderer) {
      return (
        <p className="error-text" key={request.id}>
          This screen cannot show a “{request.spec.kind}” yet.
        </p>
      );
    }
    const card = (
      <Renderer
        key={request.id}
        request={request}
        spec={request.spec}
        submit={(value) => onDecide(request.id, value)}
        nameOf={nameOf}
        disabled={request.disabled !== null}
        size={size}
        slots={slots}
      />
    );
    // No wrapper without a heading: renderers draw their own box, and a driving TV drops
    // the tray inside its own row of buttons.
    return heading ? (
      <div className={`tray-card${request.blocking ? ' is-blocking' : ''}`} key={request.id}>
        {card}
      </div>
    ) : (
      card
    );
  });

  if (!heading) return <>{cards}</>;

  return (
    <div className="panel judge-tray">
      <h2 className="panel-title">
        {heading}
        {blocking > 0 ? <span className="tray-badge">{blocking}</span> : null}
      </h2>
      {cards}
    </div>
  );
}
